import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/** Bronze → Silver → Gold: the pipeline in one picture, drawn once from docs/academy-sample-lesson.html. */
export const BronzeSilverGold: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const stages = [
    { x: 20, tone: color.bronze, name: 'Bronze', what: 'what arrived, untouched', never: 'never fixed here', rows: ['fund_admin_q2.csv · row 118', 'gp_sheet.xlsx · row 7', 'lp_portal export · row 42'] },
    { x: 360, tone: color.silver, name: 'Silver', what: 'one claim per row', never: 'never edited, only added to', rows: ['Fund III · NAV · 30 Jun · 412.0m', 'Fund III · NAV · 30 Jun · 410.5m', 'Fund III · NAV · 30 Jun · 412.0m'] },
  ];

  return (
    <svg viewBox="0 0 990 270" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Bronze → Silver → Gold" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {stages.map(({ x, tone, name, what, never, rows }) => (
        <g key={name}>
          <rect x={x} y="40" width="270" height="160" rx="12" fill="none" stroke={tone} strokeWidth="2.5" />
          <text x={x + 135} y="72" textAnchor="middle" style={lbl} fill={tone}>
            {name}
          </text>
          <text x={x + 135} y="96" textAnchor="middle" style={sub}>
            {what}
          </text>
          {rows.map((text, i) => (
            <g key={text + i}>
              <rect x={x + 20} y={118 + i * 26} width="230" height="20" rx="3" fill={tone} opacity=".18" />
              <text x={x + 30} y={133 + i * 26} style={row}>
                {text}
              </text>
            </g>
          ))}
          <text x={x + 135} y="240" textAnchor="middle" style={sub}>
            {never}
          </text>
        </g>
      ))}
      <line x1="292" y1="120" x2="356" y2="120" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow)" />
      <text x="324" y="108" textAnchor="middle" style={sub}>
        mapping
      </text>
      <line x1="632" y1="120" x2="696" y2="120" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow)" />
      <text x="664" y="108" textAnchor="middle" style={sub}>
        resolve
      </text>
      <g>
        <rect x="700" y="40" width="270" height="160" rx="12" fill="none" stroke={color.gold} strokeWidth="2.5" />
        <text x="835" y="72" textAnchor="middle" style={lbl} fill={color.gold}>
          Gold
        </text>
        <text x="835" y="96" textAnchor="middle" style={sub}>
          a view — the winning claim
        </text>
        <rect x="720" y="128" width="230" height="48" rx="6" fill={color.gold} opacity=".22" />
        <text x="835" y="148" textAnchor="middle" style={lbl} fill={color.fg}>
          Fund III NAV
        </text>
        <text x="835" y="167" textAnchor="middle" style={row}>
          412.0m · fund admin · 30 Jun
        </text>
        <text x="835" y="240" textAnchor="middle" style={sub}>
          never a table, never written
        </text>
      </g>
    </svg>
  );
};
